import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Box, CssBaseline, AppBar, Toolbar, Typography, Fade } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoIcon from '@mui/icons-material/Info';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import PoolIcon from '@mui/icons-material/Pool';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SportsList from './components/SportsList';
import Events from './components/Events';
import Leaderboard from './components/Leaderboard';
import Locations from './components/Locations';
import About from './components/About';
import SportDetails from './components/SportsDetails';
import UpcomingFixtures from './components/UpcomingFixtures';
import SplashScreen from './components/SplashScreen';
import cjsoihowtogif from './assets/cjsoisportGIF.gif';
import cjsoibg from './assets/cjsoiwave.png';
import FloatingIcons from './components/FloatingIcons';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Gallery from './components/Gallery';
import ImageIcon from '@mui/icons-material/Image';
import Schedule from './components/Schedule';



const floatAnimation = {
  '@keyframes float': {
    '100%': { transform: 'translateY(0)' },
    '0%': { transform: 'translateY(-100vh)' },
  },
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showFirstTimeGuide, setShowFirstTimeGuide] = useState(false);
  

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('seen-sport-guide');
    if (!hasSeenGuide) {
      setShowFirstTimeGuide(true);
      localStorage.setItem('seen-sport-guide', 'true');
    }
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Show prompt only on first 3 visits
      const installCount = parseInt(localStorage.getItem('pwa-install-count') || '0', 10);

      if (installCount < 30) {
        setShowInstallPrompt(true);
        localStorage.setItem('pwa-install-count', (installCount + 1).toString());
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);


  const sportsData = [
    { id: 'sport1', name: 'Football', icon: <SportsSoccerIcon /> },
    { id: 'sport2', name: 'Basketball', icon: <SportsBasketballIcon /> },
    { id: 'sport3', name: 'Volleyball', icon: <SportsVolleyballIcon /> },
    { id: 'sport4', name: 'Athletics', icon: <DirectionsRunIcon /> },
    { id: 'sport5', name: 'Judo', icon: <SportsMmaIcon /> },
    { id: 'sport6', name: 'Badminton', icon: <SportsTennisIcon /> },
    { id: 'sport7', name: 'Swimming', icon: <PoolIcon /> },
    { id: 'sport8', name: 'Boxing', icon: <SportsIcon /> },
    { id: 'sport9', name: 'Beach Soccer', icon: <SportsIcon /> },
    { id: 'sport10', name: 'Sailing', icon: <DirectionsBoatIcon /> },
    { id: 'sport11', name: 'Weightlifting', icon: <FitnessCenterIcon /> },
  ];

  const FloatingIcons = () => {
    const icons = [
      <SportsSoccerIcon fontSize="large" />,
      <SportsBasketballIcon fontSize="large" />,
      <SportsVolleyballIcon fontSize="large" />,
      <DirectionsRunIcon fontSize="large" />,
      <SportsMmaIcon fontSize="large" />,
      <SportsTennisIcon fontSize="large" />,
      <PoolIcon fontSize="large" />,
      <FitnessCenterIcon fontSize="large" />,
      <SportsIcon fontSize="large" />,
      <DirectionsBoatIcon fontSize="large" />,
    ];

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          opacity: 0.35,
          ...floatAnimation,
        }}
      >
        {icons.map((icon, index) => {
          const left = Math.random() * 90; // percentage
          const delay = Math.random() * 20; // seconds
          const duration = 20 + Math.random() * 100; // seconds

          return (
            <Box
              key={index}
              sx={{
                position: 'absolute',
                bottom: -60,
                left: `${left}%`,
                color: '#ADD8E6',
                animation: `float ${duration}s linear infinite`,
                animationDelay: `${delay}s`,
                opacity: 0.35,
              }}
            >
              {icon}
            </Box>
          );
        })}
      </Box>
    );
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };


  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowSplash(false);
    }, 2500); // 2.5 seconds

    return () => clearTimeout(timeout);
  }, []);

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <CssBaseline />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundImage: `url(${cjsoibg}),linear-gradient(159deg, rgba(0,71,171,1) 0%, rgba(28,169,201,1) 100%)`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center 95%',
          backgroundSize: 'contain',
        }}
      >
        <FloatingIcons />
        <Fade in timeout={500}>
          <Box>
            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Box sx={{ px: 2, pt: 2, pb: 2, bgcolor: 'transparent' }}>
                      <section id="sports" sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: 'white' }} gutterBottom>
                          Événements
                        </Typography>
                        <SportsList sports={sportsData} />
                      </section>
                    </Box>
                    <Box sx={{ px: 2, background: 'white', opacity:0.96,  borderTopLeftRadius: '10px', borderTopRightRadius: '10px'  }}>
                      <UpcomingFixtures />
                    </Box>
                  </>
                }
              />
              <Route
                path="/sport/:sportId"
                element={<SportDetails sportsData={sportsData} />}
              />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/about" element={<About />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/schedule" element={<Schedule />} />
              <Route
                path="*"
                element={
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#042940' }}>
                      Page Not Found
                    </Typography>
                  </Box>
                }
              />
            </Routes>
          </Box>
        </Fade>
      </Box>
      <BottomNavigation
        showLabels
        value={location.pathname}
        onChange={(event, newValue) => {
          navigate(newValue);
        }}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
          '& .MuiBottomNavigationAction-root': {
            color: 'gray',
            '&.Mui-selected': {
              color: 'rgba(28,169,201,1)',
            },
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
            },
          },
        }}
      >
        <BottomNavigationAction label="Accueil" value="/" icon={<HomeIcon />} />
        <BottomNavigationAction label="Classement" value="/leaderboard" icon={<LeaderboardIcon />} />
        <BottomNavigationAction label="Lieux" value="/locations" icon={<LocationOnIcon />} />
        <BottomNavigationAction label="Galerie" value="/gallery" icon={<ImageIcon />} />
      </BottomNavigation>
      <Dialog open={showInstallPrompt} onClose={() => setShowInstallPrompt(false)}>
        <DialogTitle>Installer l'application</DialogTitle>
        <DialogContent>
          <Typography>
            Pour une meilleure expérience, vous pouvez installer cette application sur votre appareil.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInstallPrompt(false)}>Plus tard</Button>
          <Button endIcon={<DownloadIcon />} sx={{ backgroundColor: 'rgb(0,127,255)', backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)', color: 'white', fontWeight: 'bold' }} onClick={handleInstallClick} variant="contained">
            Installer
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showFirstTimeGuide} onClose={() => setShowFirstTimeGuide(false)}>
        <DialogTitle><b>Astuce</b></DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Saviez-vous ? Vous pouvez cliquer sur un sport dans la liste pour voir plus d'informations, comme les horaires, les lieux, et les résultats en direct.
          </Typography>
          <img src={cjsoihowtogif} alt='How To' />
          <Typography variant="body2" color="text.secondary">
            Essayez-le maintenant dans la section “Événements” !
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained"
            sx={{
              backgroundColor: 'rgb(0,127,255)',
              backgroundImage: 'linear-gradient(159deg, rgba(0,127,255,1) 0%, rgba(42,82,190,1) 100%)', color: 'white', fontWeight: 'bold'
            }}
            size="small" onClick={() => setShowFirstTimeGuide(false)} autoFocus>
            J'ai compris
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default App;